import classNames from "classnames";
import BookingPill from "PFApp/components/pills/booking_pill";
import { ActionIcon } from "PFComponents/action_icon";
import SectionBox from "PFComponents/box/section_box";
import { Button } from "PFComponents/button";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import Pill from "PFComponents/pill/pill";
import { fetchPaginatedCollection } from "PFCore/helpers/collection";
// eslint-disable-next-line max-len
import { useAdminBookingCategoriesInvalidate } from "PFCore/hooks/queries/bookings/booking_categories/use_admin_booking_categories_invalidate";
import { ApiRoute } from "PFCore/utilities/routes";
import AddIcon from "PFIcons/add.svg";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./booking_categories.module.scss";
import { BookingCategoryModal } from "./booking_category_modal/booking_category_modal";

const Category = ({ category, onClick }) => {
  const yesNo = (value) => (value ? "Yes" : "No");
  const { t } = useTranslation("bookingModule");

  return (
    <div className={classNames(css.categoriesGrid, css.category)}>
      <span>
        {category.display_as}
        {category.system_defined && (
          <Pill style={{ marginLeft: 5 }}>{t("config.categories.systemDefined")}</Pill>
        )}
        {category.main && <Pill style={{ marginLeft: 5 }}>{t("main")}</Pill>}
      </span>
      <span className={css.textField}>{yesNo(category.billable)}</span>
      <span className={css.textField}>{yesNo(category.affects_availability)}</span>
      <span className={css.textField}>{yesNo(category.hidden)}</span>
      <BookingPill category={category} style={{ height: 20, width: 80 }} />
      <ActionIcon
        name="edit"
        size="md"
        onClick={() => onClick(category)}
        classes={{ root: css.editButton }}
      />
    </div>
  );
};

Category.propTypes = {
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    display_as: PropTypes.string.isRequired,
    billable: PropTypes.bool.isRequired,
    main: PropTypes.bool.isRequired,
    affects_availability: PropTypes.bool.isRequired,
    color: PropTypes.string.isRequired,
    system_defined: PropTypes.bool.isRequired,
    hidden: PropTypes.bool.isRequired
  }),
  onClick: PropTypes.func.isRequired
};

const BookingCategories = ({ onConfigUpdate }) => {
  const [categoryModalData, setCategoryModalData] = useState({
    isOpened: false,
    category: null
  });
  const [categories, setCategories] = useState({
    entries: [],
    meta: {
      total: 0
    }
  });
  const [archivedCategories, setArchivedCategories] = useState({
    entries: [],
    meta: {
      total: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation("bookingModule");
  const { invalidate: invalidateAdminBookingCategories } = useAdminBookingCategoriesInvalidate();

  const url = ApiRoute("/api/admin/booking_module/booking_categories");
  // TODO: [ENG-3017] use react query hook useAdminBookingCategories
  const fetchCategories = (params) => {
    setLoading(true);
    fetchPaginatedCollection(url, { archived: false, perPage: 10, ...params }).then((result) => {
      setCategories(result);
      setLoading(false);
    });
  };

  const fetchArchivedCategories = (params) => {
    setLoading(true);
    fetchPaginatedCollection(url, { archived: true, perPage: 10, ...params }).then((result) => {
      setArchivedCategories(result);
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchCategories();
    fetchArchivedCategories();
  }, []);

  const anyCategories = categories.entries.length > 0 || archivedCategories.entries.length > 0;

  return (
    <>
      <div>
        <Button
          kind="primary"
          onClick={() => {
            setCategoryModalData({
              isOpened: true,
              category: null
            });
          }}
          className={css.addButton}
        >
          <AddIcon width={20} height={20} />
          {t("config.categories.addButton")}
        </Button>
        <SectionBox className={classNames(css.categoriesList)} isLoading={loading}>
          {anyCategories && (
            <div className={classNames(css.headers, css.categoriesGrid)}>
              <span>{t("name")}</span>
              <span>{t("billable")}</span>
              <span>{t("affectsAvailability")}</span>
              <span>{t("config.categories.hiddenBookings")}</span>
              <span>{t("colour")}</span>
            </div>
          )}
          {categories.entries.map((category) => (
            <Category
              key={category.id}
              category={category}
              onClick={() => {
                setCategoryModalData({
                  isOpened: true,
                  category
                });
              }}
            />
          ))}

          <SimplePagination
            currentPage={categories.meta.page}
            totalPages={categories.meta.totalPages}
            handleClick={(page) => fetchCategories({ page })}
            scrollOnClick={false}
          />
          {archivedCategories.entries.length > 0 && <h3 className={css.subGroupTitle}>{t("archive")}</h3>}
          {archivedCategories.entries.map((category) => (
            <Category
              key={category.id}
              category={category}
              onClick={() => {
                setCategoryModalData({
                  isOpened: true,
                  category
                });
              }}
            />
          ))}
          <SimplePagination
            currentPage={archivedCategories.meta.page}
            totalPages={archivedCategories.meta.totalPages}
            handleClick={(page) => fetchArchivedCategories({ page })}
            scrollOnClick={false}
          />
        </SectionBox>
      </div>
      {categoryModalData.isOpened && (
        <BookingCategoryModal
          category={categoryModalData.category}
          onClose={() => {
            setCategoryModalData({
              isOpened: false,
              category: null
            });
          }}
          onSaved={() => {
            fetchCategories();
            fetchArchivedCategories();
            invalidateAdminBookingCategories();
            onConfigUpdate?.();
          }}
        />
      )}
    </>
  );
};

BookingCategories.propTypes = {
  onConfigUpdate: PropTypes.func
};

export default BookingCategories;
